import React, { useState } from 'react';

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const faqData = [
    {
      question: "What are the common symptoms of urological problems?",
      answer: "Common symptoms include frequent urination, burning sensation during urination, blood in urine, lower abdominal pain, difficulty urinating, and changes in urine color or smell."
    },
    {
      question: "How are kidney stones treated?",
      answer: "Kidney stones are treated based on size and location. Small stones may pass naturally with medication and increased fluid intake. Larger stones may require procedures like ESWL, ureteroscopy, or PCNL."
    },
    {
      question: "What is prostate enlargement and how is it treated?",
      answer: "Prostate enlargement (BPH) is common in aging men. Treatment options include medication, minimally invasive procedures like TURP, laser surgery, and in some cases, open surgery."
    },
    {
      question: "Are urinary tract infections serious?",
      answer: "UTIs should be taken seriously as untreated infections can spread to the kidneys and cause severe complications. Prompt diagnosis and proper antibiotic treatment are essential."
    },
    {
      question: "What are the warning signs of bladder cancer?",
      answer: "Warning signs include blood in urine (hematuria), frequent urination, painful urination, back pain, and pelvic pain. Early detection significantly improves treatment outcomes."
    },
    {
      question: "Can erectile dysfunction be treated?",
      answer: "Yes, erectile dysfunction can be effectively treated. Options include oral medications, lifestyle changes, vacuum devices, injections, penile implants, and addressing underlying health conditions."
    },
    {
      question: "What causes urinary incontinence?",
      answer: "Urinary incontinence can result from various factors including aging, childbirth, prostate problems, neurological disorders, and certain medications."
    },
    {
      question: "How often should men get urological check-ups?",
      answer: "Men over 40 should have annual urological check-ups. Those with risk factors like family history of prostate cancer may need more frequent evaluations."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-10 md:py-14 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-4 md:px-6">
        
        {/* Header Section */}
        <div className="text-center mb-8 md:mb-10">
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-800 mb-2">
            Frequently Asked Questions
          </h1>
          <div className="w-16 h-0.5 bg-gradient-to-r from-blue-600 to-cyan-500 mx-auto mb-3 rounded-full"></div>
          <p className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto">
            Common questions about urological health and treatments
          </p>
        </div>

        {/* FAQ Grid - 2 per row on desktop, 1 on mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 max-w-5xl mx-auto">
          {faqData.map((faq, index) => (
            <div 
              key={index}
              className={`bg-white rounded-lg border transition-all duration-300 transform hover:scale-[1.02] hover:shadow-lg ${
                openIndex === index 
                  ? 'border-blue-300 shadow-md scale-[1.02]' 
                  : 'border-gray-200 hover:border-blue-200'
              }`}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {/* FAQ Item */}
              <button
                className="flex justify-between items-start w-full p-3 md:p-4 text-left focus:outline-none group"
                onClick={() => toggleFAQ(index)}
              >
                <div className="flex items-start w-full">
                  {/* Number Badge */}
                  <div className="flex-shrink-0">
                    <div className={`w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                      openIndex === index 
                        ? 'bg-blue-600 text-white' 
                        : hoveredIndex === index
                        ? 'bg-blue-100 text-blue-600'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      <span className="text-xs md:text-sm font-bold">{index + 1}</span>
                    </div>
                  </div>
                  
                  {/* Question */}
                  <div className="ml-3 md:ml-4 flex-1">
                    <h3 className="text-sm md:text-base font-semibold text-gray-800 group-hover:text-blue-700 transition-colors duration-200">
                      {faq.question}
                    </h3>
                    
                    {/* Progress Bar Animation */}
                    <div className="mt-2 h-0.5 w-full bg-gray-100 overflow-hidden">
                      <div className={`h-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all duration-500 ${
                        openIndex === index ? 'w-full' : hoveredIndex === index ? 'w-1/2' : 'w-0'
                      }`}></div>
                    </div>
                  </div>
                  
                  {/* Expand/Collapse Icon */}
                  <div className="flex-shrink-0 ml-2">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 transform ${
                      openIndex === index 
                        ? 'bg-blue-100 text-blue-600 rotate-180' 
                        : 'bg-gray-100 text-gray-600 group-hover:bg-blue-50 group-hover:text-blue-600'
                    }`}>
                      <svg 
                        className={`w-3 h-3 transition-transform duration-300 ${
                          openIndex === index ? 'rotate-180' : ''
                        }`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </button>
              
              {/* Answer with Animation */}
              <div className={`overflow-hidden transition-all duration-500 ease-in-out ${
                openIndex === index 
                  ? 'max-h-96 opacity-100 translate-y-0' 
                  : 'max-h-0 opacity-0 -translate-y-2'
              }`}>
                <div className="px-3 md:px-4 pb-3 md:pb-4">
                  <div className="flex">
                    <div className="flex-shrink-0 w-6 md:w-8 flex items-start justify-center">
                      <div className="w-2 h-2 rounded-full bg-green-500 mt-2 animate-pulse"></div>
                    </div>
                    <div className="ml-3 md:ml-4">
                      <div className="text-gray-600 text-xs md:text-sm leading-relaxed">
                        {faq.answer}
                      </div>
                      
                      {/* Learn More Link */}
                      <div className="mt-2">
                        <button className="text-blue-600 text-xs font-medium hover:text-blue-800 transition-colors duration-200 inline-flex items-center">
                          Learn more
                          <svg className="w-3 h-3 ml-1 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        @keyframes bounceOnce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-5px);
          }
        }
        .animate-bounce-once {
          animation: bounceOnce 2s ease-in-out infinite;
        }
        .hover\:scale-\[1\.02\]:hover {
          transform: scale(1.02);
        }
        .scale-\[1\.02\] {
          transform: scale(1.02);
        }
      `}</style>
    </section>
  );
};

export default FAQSection;